import {
  EmploymentStatusEnum,
  TranslationLookups,
  UserModel,
} from 'api/model';
import renderApiValue from 'utils/render-api-value';
import { useExportExportUsers } from 'api/export/export';
import { useAuthenticationResendConfirmationEmailHook } from 'api/authentication/authentication';
import AuthorizedButton from 'components/inputs/AuthorizedButton';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import Table from 'components/Table';
import { ColumnSetting, makeColumnClassName } from 'components/Table/utils';
import useTranslations from 'contexts/basicData/useTranslations';
import useUsers, { useRefreshUsersCall } from 'contexts/basicData/useUsers';
import { FC, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import UserFormModal from '../UserFormModal';
import {
  faEnvelope,
  faFileExcel,
  faLocationDot,
  faSpinner,
  faUserFriends,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import { downloadBlob } from 'utils/file-utils';
import Roles from 'constants/Roles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  booleanCompare,
  makeBooleanComparator,
  makeNumberComparator,
  makeStringComparator,
} from 'utils/sorting';
import { convertToDateOfBirthString, formatDateTime } from 'utils/date-helpers';
import Checkbox from 'components/inputs/Checkbox';
import { useLocalStorage } from 'usehooks-ts';
import Input from 'components/inputs/Input';
import userPropsIconDictionary from '../filterableUserFields';
import { WarningInfo } from 'hooks/useAssignmentWarnings';
import ExpandableWarningsWidget from 'components/AssignmentsList/ExpandableWarningsWidget';
import { UserFormField } from '../useUserForm';
import BusinessUnitSelect from 'components/inputs/BusinessUnitSelect';

const Wrapper = styled.div``;

const Center = styled.div`
  display: flex;
  justify-content: center;
`;

// const EmojiWrapper = styled.div<{ amount: number }>`
//   ${({ amount }) => css`
//     display: grid;
//     grid-template-columns: repeat(${amount}, 1fr);
//     grid-auto-flow: row;
//   `}
// `;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 50px;
  padding: 10px;
  gap: 10px;
`;

const EnvelopeButton = styled.button`
  outline: none;
  border: none;
  background: transparent;
  line-height: 12px;
  width: 100%;

  .fa-icon {
    padding-right: 6px;
  }

  .fa-icon:hover {
    color: ${({ theme }) => theme.colors.foreground.tint};
  }
`;

const MyCheckbox = styled(Checkbox)`
  padding-right: 10px;
  display: flex;
  align-items: center;
`;

const MyTable = styled(Table)`
  .${makeColumnClassName(0)} {
    padding-left: 10px;
  }
` as typeof Table;

const EmployeesPage: FC = () => {
  const translations = useTranslations();
  const { rolesById } = translations;
  const users = useUsers();
  const refreshUsersCall = useRefreshUsersCall();
  const [showAddUser, setShowAddUser] = useState(false);
  const [editUser, setEditUser] = useState<UserModel | null>(null);
  const [sentMailTo, setSentMailTo] = useState<number[]>([]);
  const [hideInactive, setHideInactive] = useLocalStorage<boolean>(
    'hide-inactive-users',
    true
  );
  const [search, setSearch] = useState<string>('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<Set<number>>(
    new Set()
  );
  // HR:s kontrollvy: anställda som är avstängda för bokning. Medvetet inte sparad i
  // localStorage — ett filter som ligger kvar tyst mellan besök döljer personal.
  const [onlyEmployedUnbookable, setOnlyEmployedUnbookable] = useState(false);
  const exportUsersMutation = useExportExportUsers();

  const toggle = useRef(true);

  const resendConfirmationEmail = useAuthenticationResendConfirmationEmailHook();
  const resendCall = useApiCallFn((id: number) =>
    resendConfirmationEmail({ userId: id })
  );

  const handleUsersUpdated = useCallback(() => {
    setShowAddUser(false);
    setEditUser(null);
    refreshUsersCall.run();
  }, [refreshUsersCall]);

  const handleExcelDownload = useCallback(async () => {
    const businessUnitIds =
      businessUnitFilter.size > 0 ? Array.from(businessUnitFilter) : undefined;
    const blob = await exportUsersMutation.mutateAsync({
      data: { hideInactive, businessUnitIds },
    });
    if (blob) {
      const today = new Date().toISOString().split('T')[0];
      downloadBlob(blob, `Zäkra_personal_${today}.xlsx`);
    }
  }, [exportUsersMutation, hideInactive, businessUnitFilter]);

  const handleResend = useCallback(
    async (id: number) => {
      if (!sentMailTo.includes(id)) {
        const [response, error] = await resendCall.run(id);
        if (response && !error) {
          setSentMailTo([...sentMailTo, id]);
        }
      }
      toggle.current = true;
    },
    [resendCall, sentMailTo]
  );

  const getWarnings = useCallback(
    (user: UserModel): WarningInfo[] => {
      const warnings: WarningInfo[] = [];
      // check if email has been confirmed for active users
      if (!user.emailConfirmed && user.isActive) {
        warnings.push({
          warning: 'Ej bekräftad e-post',
          icon: <FontAwesomeIcon icon={faEnvelope} />,
        });
      }

      // check if the user might be a duplicate (has the same dateOfBirth and phone number as another user)
      const duplicates = Object.values(users).filter(
        (u) =>
          user.isActive && // Current user must be active
          user.isActive === u.isActive && // and the other as well
          u.userID !== user.userID &&
          // phoneNumber is typed string but arrives as null for users without one
          !!user.phoneNumber &&
          u.phoneNumber === user.phoneNumber &&
          u.dateOfBirth &&
          user.dateOfBirth &&
          convertToDateOfBirthString(u.dateOfBirth.toString()) ===
            convertToDateOfBirthString(user.dateOfBirth.toString())
      );
      if (duplicates.length > 0) {
        warnings.push({
          warning: 'Möjlig dublett',
          icon: <FontAwesomeIcon icon={faUserFriends} />,
          details: duplicates.map((u) => u.name).join(', '),
        });
      }

      // Ruttplaneringen räknar dagen från och till hemankaret och läser inte bostadsadressen,
      // så en fälttestare utan hemankare ingår inte i den automatiska planeringen. Det får inte
      // upptäckas paket för paket i planeringsvyn — därav varningen här.
      // Rollen matchas på namn, precis som backend gör; samma roll heter Roles.User (id 2) i
      // frontendens konstanter, vilket är missvisande men etablerat.
      const isFieldTester = user.roles.some(
        (roleId) => rolesById[roleId] === 'Fälttestare'
      );
      // Listan när den finns (personallistan fyller den); det gamla enkelfältet är reserven.
      const hasHomeAnchor =
        (user.homeAnchorCompanyIDs?.length ?? 0) > 0 ||
        user.homeAnchorCompanyID != null;
      if (user.isActive && isFieldTester && !hasHomeAnchor) {
        warnings.push({
          warning: 'Saknar hemankare – ingår inte i ruttplaneringen',
          icon: <FontAwesomeIcon icon={faLocationDot} />,
        });
      }

      return warnings;
    },
    [users, rolesById]
  );

  const columnSettings: ColumnSetting<UserModel, TranslationLookups>[] =
    useMemo(
      () => [
        {
          head: 'Nr',
          render: (usr, props, focused, index) => index + 1,
          width: 20,
        },
        {
          head: <FontAwesomeIcon icon={faWarning} />,
          csvHead: 'Varningar',
          csvValue: (usr) =>
            getWarnings(usr)
              .map((w) => w.warning)
              .join(', '),
          render: (usr) => {
            const warnings = getWarnings(usr);
            return (
              <Center>
                <ExpandableWarningsWidget warnings={warnings} />
              </Center>
            );
          },

          width: 20,
          sortFunction: makeNumberComparator((usr) => getWarnings(usr).length),
        },
        {
          head: 'Namn',
          csvHead: 'Namn',
          csvValue: (usr) => usr.name,
          render: (usr) => usr.name,
          width: 250,
          sortFunction: (a, b) => (a.name > b.name ? 1 : -1),
        },
        {
          head: 'Enhet',
          csvHead: 'Enhet',
          csvValue: (usr) => usr.businessUnitName ?? '',
          render: (usr) => usr.businessUnitName ?? '',
          width: 80,
          sortFunction: makeStringComparator(
            (usr) => usr.businessUnitName ?? ''
          ),
        },
        {
          head: 'Grönskylt',
          csvHead: 'Grönskylt',
          csvValue: (usr) => usr.greenPlate ?? '-',
          render: (usr) => usr.greenPlate,
          width: 80,
          sortFunction: makeStringComparator((usr) => usr.greenPlate ?? ''),
        },
        {
          head: 'Anställningsnr',
          csvHead: 'Anställningsnr',
          csvValue: (usr) => usr.employmentID ?? '-',
          render: (usr) => usr.employmentID,
          width: 80,
          sortFunction: (a, b) =>
            (a.employmentID ?? '').localeCompare(b.employmentID ?? ''),
        },
        {
          head: 'Telefon',
          csvHead: 'Telefon',
          csvValue: (usr) => usr.phoneNumber ?? '-',
          render: (usr) => usr.phoneNumber,
          width: 100,
          sortFunction: (a, b) => (a.phoneNumber > b.phoneNumber ? 1 : -1),
        },
        {
          head: 'E-post',
          csvHead: 'E-post',
          csvValue: (usr) => usr.email ?? '-',
          render: (usr) => usr.email,
          width: 250,
          sortFunction: (a, b) => (a.email > b.email ? 1 : -1),
        },
        {
          head: 'Bjud in igen',
          render: (usr) =>
            !usr.emailConfirmed && !sentMailTo.includes(usr.userID) ? (
              <EnvelopeButton type="submit">
                <FontAwesomeIcon
                  className="fa-icon"
                  icon={faEnvelope}
                  onClick={() => {
                    toggle.current = false;
                    handleResend(usr.userID);
                  }}
                  style={{ zIndex: 1000 }}
                />
              </EnvelopeButton>
            ) : (
              ''
            ),
          width: 40,
        },
        {
          head: 'Roller',
          render: (usr, transl) =>
            usr.roles.map((r) => transl.rolesById[r]).join(', '),
          width: 120,
          sortFunction: (a, b) => a.roles.length - b.roles.length,
        },
        {
          head: '',
          csvHead:
            userPropsIconDictionary[UserFormField.canTestPremiumCars].title,
          csvValue: (usr) => (usr.canTestPremiumCars ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canTestPremiumCars].icon(
              usr.canTestPremiumCars
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canTestPremiumCars),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.canTestTrucks].title,
          csvValue: (usr) => (usr.canTestTrucks ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canTestTrucks].icon(
              usr.canTestTrucks
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canTestTrucks),
        },
        {
          head: '',
          csvHead:
            userPropsIconDictionary[UserFormField.canDoHomeDelivery].title,
          csvValue: (usr) => (usr.canDoHomeDelivery ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDoHomeDelivery].icon(
              usr.canDoHomeDelivery
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDoHomeDelivery),
        },
        {
          head: '',
          csvHead:
            userPropsIconDictionary[UserFormField.canDriveElectric].title,
          csvValue: (usr) => (usr.canDriveElectric ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDriveElectric].icon(
              usr.canDriveElectric
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDriveElectric),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.canDriveManual].title,
          csvValue: (usr) => (usr.canDriveManual ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDriveManual].icon(
              usr.canDriveManual
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDriveManual),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.canDriveTrucks].title,
          csvValue: (usr) => (usr.canDriveTrucks ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDriveTrucks].icon(
              usr.canDriveTrucks
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDriveTrucks),
        },
        {
          head: '',
          csvHead:
            userPropsIconDictionary[UserFormField.canDriveCarTransportTrucks]
              .title,
          csvValue: (usr) => (usr.canDriveCarTransportTrucks ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[
              UserFormField.canDriveCarTransportTrucks
            ].icon(usr.canDriveCarTransportTrucks),
          width: 15,
          sortFunction: makeBooleanComparator(
            (usr) => usr.canDriveCarTransportTrucks
          ),
        },
        {
          head: '',
          csvHead:
            userPropsIconDictionary[UserFormField.canDriveTrailers].title,
          csvValue: (usr) => (usr.canDriveTrailers ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDriveTrailers].icon(
              usr.canDriveTrailers
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDriveTrailers),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.canDrivePremium].title,
          csvValue: (usr) => (usr.canDrivePremium ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDrivePremium].icon(
              usr.canDrivePremium
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDrivePremium),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.canDoLongDrives].title,
          csvValue: (usr) => (usr.canDoLongDrives ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canDoLongDrives].icon(
              usr.canDoLongDrives
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canDoLongDrives),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.isFullTime].title,
          csvValue: (usr) => (usr.isFullTime ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.isFullTime].icon(
              usr.isFullTime
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.isFullTime),
        },
        {
          head: '',
          csvHead:
            userPropsIconDictionary[UserFormField.certifiedToCallCustomers]
              .title,
          csvValue: (usr) => (usr.certifiedToCallCustomers ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[
              UserFormField.certifiedToCallCustomers
            ].icon(usr.certifiedToCallCustomers),
          width: 15,
          sortFunction: makeBooleanComparator(
            (usr) => usr.certifiedToCallCustomers
          ),
        },
        {
          head: '',
          csvHead: userPropsIconDictionary[UserFormField.canStayAtHotels].title,
          csvValue: (usr) => (usr.canStayAtHotels ? 'Ja' : 'Nej'),
          render: (usr) =>
            userPropsIconDictionary[UserFormField.canStayAtHotels].icon(
              usr.canStayAtHotels
            ),
          width: 15,
          sortFunction: makeBooleanComparator((usr) => usr.canStayAtHotels),
        },
        {
          head: 'Autogodkänn uppdrag',
          csvHead: 'Autogodkänn uppdrag',
          csvValue: (usr) => (usr.autoApproveAssignment ? 'Ja' : 'Nej'),
          render: (usr) => (usr.autoApproveAssignment ? 'Ja' : 'Nej'),
          width: 180,
          sortFunction: (a, b) =>
            a.autoApproveAssignment === b.autoApproveAssignment
              ? 0
              : a.autoApproveAssignment
                ? -1
                : 1,
        },
        {
          head: 'Visa i fakturaunderlag',
          csvHead: 'Visa i fakturaunderlag',
          csvValue: (usr) => (usr.showInInvoicing ? 'Ja' : 'Nej'),
          render: (usr) => (usr.showInInvoicing ? 'Ja' : 'Nej'),
          width: 180,
          sortFunction: makeBooleanComparator((usr) => usr.showInInvoicing),
        },
        {
          head: 'App-version',
          csvHead: 'App-version',
          csvValue: (usr) => usr.currentAppVersion ?? '-',
          render: (usr) => usr.currentAppVersion,
          width: 100,
          sortFunction: (a, b) => {
            return `${a.currentAppVersion}`.localeCompare(
              `${b.currentAppVersion}`
            );
          },
        },
        {
          head: 'Senast inloggad',
          csvHead: 'Senast inloggad',
          csvValue: (usr) => formatDateTime(usr.lastSeen),
          render: (usr) => formatDateTime(usr.lastSeen),
          width: 120,
          sortFunction: (a, b) => {
            return (a.lastSeen?.getTime() ?? 0) - (b.lastSeen?.getTime() ?? 0);
          },
        },
        {
          head: 'Betatestare',
          csvHead: 'Betatestare',
          csvValue: (usr) => (usr.isBetaTester ? 'Ja' : 'Nej'),
          render: (usr) => (usr.isBetaTester ? 'Ja' : 'Nej'),
          width: 40,
          sortFunction: (a, b) =>
            booleanCompare(!!a.isBetaTester, !!b.isBetaTester),
        },
        {
          head: 'Anställningsstatus',
          csvHead: 'Anställningsstatus',
          // csvValue får (row, rowIndex) — översättningarna kommer från closuren.
          csvValue: (usr) =>
            translations.employmentStatuses[usr.employmentStatusID] ?? '-',
          render: (usr, transl) =>
            transl.employmentStatuses[usr.employmentStatusID] ?? '-',
          width: 110,
          sortFunction: makeNumberComparator((usr) => usr.employmentStatusID),
        },
        {
          head: 'Bokningsbar',
          csvHead: 'Bokningsbar',
          csvValue: (usr) => (usr.isAvailableForDriving ? 'Ja' : 'Nej'),
          render: (usr) => (usr.isAvailableForDriving ? 'Ja' : 'Nej'),
          width: 80,
          sortFunction: makeBooleanComparator(
            (usr) => !!usr.isAvailableForDriving
          ),
        },
        {
          head: 'Anledning (ej bokningsbar)',
          csvHead: 'Anledning (ej bokningsbar)',
          // handleDates() kan ha gjort om anledningen till ett Date om någon skrivit
          // ett datum som anledning — renderas den rått vitnar hela personallistan.
          csvValue: (usr) => renderApiValue(usr.unavailableForDrivingReason),
          render: (usr) => renderApiValue(usr.unavailableForDrivingReason),
          width: 200,
          sortFunction: makeStringComparator(
            (usr) => usr.unavailableForDrivingReason ?? ''
          ),
        },
      ],
      [getWarnings, handleResend, sentMailTo, translations]
    );

  const renderContent = useCallback(() => {
    switch (refreshUsersCall.status) {
      case RequestStatus.Idle:
      case RequestStatus.Fetched:
      case RequestStatus.Error: {
        let rows = Object.values(users);
        if (hideInactive) {
          rows = rows.filter((r) => r.isActive);
        }
        if (onlyEmployedUnbookable) {
          rows = rows.filter(
            (r) =>
              r.employmentStatusID === EmploymentStatusEnum.Employed &&
              !r.isAvailableForDriving
          );
        }
        if (businessUnitFilter.size > 0) {
          rows = rows.filter(
            (r) =>
              r.businessUnitID != null &&
              businessUnitFilter.has(r.businessUnitID)
          );
        }
        if (search.length > 0) {
          rows = rows.filter(
            (r) =>
              r.name?.toLowerCase().includes(search.toLowerCase()) ||
              r.email?.toLowerCase().includes(search.toLowerCase()) ||
              r.phoneNumber?.toLowerCase().includes(search.toLowerCase()) ||
              r.employmentID?.toLowerCase().includes(search.toLowerCase()) ||
              r.greenPlate?.toLowerCase().includes(search.toLowerCase())
          );
        }
        return (
          <MyTable
            columnSettings={columnSettings}
            fluid
            onRowClick={(u) => setEditUser(u)}
            renderProps={translations}
            rows={rows}
            useColumnWidthAsFlexAmount={
              // when device width > 768
              window.innerWidth > 768
            }
          />
        );
      }
      case RequestStatus.Fetching:
      default:
        return <LoadingSpinner>Hämtar...</LoadingSpinner>;
    }
  }, [
    columnSettings,
    hideInactive,
    onlyEmployedUnbookable,
    businessUnitFilter,
    refreshUsersCall.status,
    search,
    translations,
    users,
  ]);

  return (
    <Suspense fallback="Laddar...">
      <Wrapper>
        <Actions>
          <MyCheckbox
            checked={hideInactive}
            onChange={(event) => setHideInactive(event.currentTarget.checked)}
          >
            Dölj inaktiva
          </MyCheckbox>
          <MyCheckbox
            checked={onlyEmployedUnbookable}
            onChange={(event) =>
              setOnlyEmployedUnbookable(event.currentTarget.checked)
            }
          >
            Endast anställda som ej är bokningsbara
          </MyCheckbox>
          <BusinessUnitSelect
            onChange={setBusinessUnitFilter}
            value={businessUnitFilter}
          />
          <AuthorizedButton
            disabled={refreshUsersCall.status === RequestStatus.Fetching}
            onClick={() => setShowAddUser(true)}
          >
            + Ny Användare
          </AuthorizedButton>

          <Input
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrera på namn, anställningsnr, e-post eller telefonnummer"
            value={search}
            width={300}
          />

          <AuthorizedButton
            disabled={exportUsersMutation.isLoading}
            onClick={handleExcelDownload}
            roles={[
              Roles.SuperAdmin,
              Roles.HR,
              Roles.Ekonomi,
              Roles.KvalitetOUtbildning,
            ]}
          >
            <FontAwesomeIcon
              icon={exportUsersMutation.isLoading ? faSpinner : faFileExcel}
              spin={exportUsersMutation.isLoading}
              style={{ marginRight: 6 }}
            />
            Ladda ner Excel
          </AuthorizedButton>
        </Actions>

        {showAddUser && toggle.current && (
          <UserFormModal
            canEdit
            onCancel={() => setShowAddUser(false)}
            onSaved={handleUsersUpdated}
          />
        )}
        {editUser && toggle.current && (
          <UserFormModal
            canEdit
            onCancel={() => setEditUser(null)}
            onSaved={handleUsersUpdated}
            user={editUser}
          />
        )}

        {renderContent()}
      </Wrapper>
    </Suspense>
  );
};

export default EmployeesPage;
